// ============================================================
// Settings → Collection domain (first-party ingest, docs/first-party.md):
// /v2/projects/:id/collection-domain (get / put / refresh / delete).
// ============================================================

import { v2Api } from './v2Api'
import { unwrap, type V2Envelope } from './v2Mappers'

export interface CollectionDomainRecord {
    type: string
    name: string
    value: string
}

export interface CollectionDomainState {
    custom_hostname: { available: boolean; reason?: string }
    domain: null | {
        hostname: string
        status: string
        ssl_status: string | null
        live: boolean
        records: CollectionDomainRecord[]
        errors: string[]
        endpoint: string
        script_src: string
        updated_at: string
    }
    reverse_proxy: { path: string; endpoint: string; upstream: string; docs: string }
}

const url = (projectId: string) => `/projects/${encodeURIComponent(projectId)}/collection-domain`

export const collectionDomainApi = v2Api
    .enhanceEndpoints({ addTagTypes: ['CollectionDomain'] })
    .injectEndpoints({
        endpoints: builder => ({
            getCollectionDomain: builder.query<CollectionDomainState, string>({
                query: url,
                transformResponse: (res: V2Envelope<CollectionDomainState>) => unwrap(res),
                providesTags: ['CollectionDomain'],
            }),
            setCollectionDomain: builder.mutation<
                CollectionDomainState,
                { projectId: string; hostname: string }
            >({
                query: a => ({
                    url: url(a.projectId),
                    method: 'PUT',
                    body: { hostname: a.hostname },
                }),
                transformResponse: (res: V2Envelope<CollectionDomainState>) => unwrap(res),
                invalidatesTags: ['CollectionDomain'],
            }),
            refreshCollectionDomain: builder.mutation<CollectionDomainState, string>({
                query: projectId => ({ url: `${url(projectId)}/refresh`, method: 'POST' }),
                transformResponse: (res: V2Envelope<CollectionDomainState>) => unwrap(res),
                invalidatesTags: ['CollectionDomain'],
            }),
            removeCollectionDomain: builder.mutation<CollectionDomainState, string>({
                query: projectId => ({ url: url(projectId), method: 'DELETE' }),
                transformResponse: (res: V2Envelope<CollectionDomainState>) => unwrap(res),
                invalidatesTags: ['CollectionDomain'],
            }),
        }),
    })

export const {
    useGetCollectionDomainQuery,
    useSetCollectionDomainMutation,
    useRefreshCollectionDomainMutation,
    useRemoveCollectionDomainMutation,
} = collectionDomainApi
